import { __ } from "@wordpress/i18n"
import { 
  useBlockProps, 
  InspectorControls,
  RichText
} from "@wordpress/block-editor"
import { 
  PanelBody,
  ToggleControl,
  RangeControl,
  TextControl,
  CheckboxControl,
  Spinner
} from "@wordpress/components"
import { useSelect } from "@wordpress/data"
import ServerSideRender from "@wordpress/server-side-render"
import { useState, useMemo } from "@wordpress/element"
import { registerBlockType } from "@wordpress/blocks"

function Edit({ attributes, setAttributes, clientId }) {
  const blockProps = useBlockProps({
    className: `white-papers-block ${attributes.align ? `align${attributes.align}` : ''}`
  })

  const [isEditing, setIsEditing] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  // Fetch all white paper posts
  const whitePaperPosts = useSelect((select) => {
    return select("core").getEntityRecords("postType", "whitepaper", {
      per_page: -1,
      status: "publish"
    })
  }, [])

  const isLoading = whitePaperPosts === null
  
  // Create options for white paper selection
  const whitePaperOptions = whitePaperPosts
    ? whitePaperPosts.map((post) => ({
        label: post.title?.rendered || `#${post.id}`,
        value: String(post.id)
      }))
    : []

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    if (!q) return whitePaperOptions
    return whitePaperOptions.filter((opt) =>
      opt.label.toLowerCase().includes(q)
    )
  }, [whitePaperOptions, searchTerm])

  const selected = attributes.selectedWhitePapers || []
  const maxSelect = 6

  // Handle selection toggle
  const toggleSelect = (value) => {
    const has = selected.includes(value)
    if (has) {
      setAttributes({ selectedWhitePapers: selected.filter((v) => v !== value) })
    } else if (selected.length < maxSelect) {
      setAttributes({ selectedWhitePapers: [...selected, value] })
    }
  }

  // If we're editing, show the rich text editors instead of ServerSideRender
  if (isEditing) {
    return (
      <>
        <InspectorControls>
          <PanelBody title={__('White Papers Settings', 'puroclean-tw')} initialOpen={true}>
            {/* Show All Papers Toggle */}
            <ToggleControl
              label={__('Show All White Papers', 'puroclean-tw')}
              checked={attributes.showAllPapers}
              onChange={(value) => setAttributes({ showAllPapers: value })}
              help={__('Toggle to show all papers or select specific ones', 'puroclean-tw')}
            />

            {attributes.showAllPapers ? (
              /* Posts Per Page Setting */
              <RangeControl
                label={__('Number of Papers to Show', 'puroclean-tw')}
                value={attributes.postsPerPage}
                onChange={(value) => setAttributes({ postsPerPage: value })}
                min={1}
                max={whitePaperOptions.length > 0 ? whitePaperOptions.length : 12}
                help={__('How many white papers to display', 'puroclean-tw')}
              />
            ) : (
              /* White Paper Selection */
              <div style={{ marginTop: 12 }}>
                <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
                  {__("Pick White Papers", "puroclean-tw")} ({selected.length}/{maxSelect})
                </label>

                <TextControl
                  placeholder={__("Search…", "puroclean-tw")}
                  value={searchTerm}
                  onChange={setSearchTerm}
                />

                {isLoading && <Spinner />}

                {!isLoading && (
                  <div
                    style={{
                      maxHeight: 240,
                      overflow: "auto",
                      border: "1px solid #ddd",
                      borderRadius: 4,
                      padding: 8,
                      marginTop: 8,
                    }}
                  >
                    {filteredOptions.length === 0 && (
                      <p style={{ margin: 0, color: "#666" }}>
                        {__("No results", "puroclean-tw")}
                      </p>
                    )}

                    {filteredOptions.map((opt) => (
                      <CheckboxControl
                        key={opt.value}
                        label={opt.label}
                        checked={selected.includes(opt.value)}
                        onChange={() => toggleSelect(opt.value)}
                        disabled={!selected.includes(opt.value) && selected.length >= maxSelect}
                      />
                    ))}
                  </div>
                )}

                <p style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                  {__("Select up to 6 items.", "puroclean-tw")}
                </p>
              </div>
            )}
          </PanelBody>
        </InspectorControls>

        <div {...blockProps}>
          <div style={{ 
            border: '2px dashed #ddd', 
            padding: '40px 20px', 
            textAlign: 'center',
            backgroundColor: '#f9f9f9'
          }}>
            <h3 style={{ marginBottom: '20px', color: '#333' }}>
              {__('White Papers Block - Edit Mode', 'puroclean-tw')}
            </h3>
            
            <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'left' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  {__('Section Title:', 'puroclean-tw')}
                </label>
                <RichText
                  tagName="div"
                  placeholder={__('Enter section title...', 'puroclean-tw')}
                  value={attributes.sectionTitle}
                  onChange={(value) => setAttributes({ sectionTitle: value })}
                  style={{ 
                    border: '1px solid #ddd', 
                    padding: '12px', 
                    minHeight: '50px',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '30px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  {__('Section Description (optional):', 'puroclean-tw')}
                </label>
                <RichText
                  tagName="div"
                  placeholder={__('Enter section description (optional)...', 'puroclean-tw')}
                  value={attributes.sectionDescription}
                  onChange={(value) => setAttributes({ sectionDescription: value })}
                  style={{ 
                    border: '1px solid #ddd', 
                    padding: '12px', 
                    minHeight: '60px',
                    fontSize: '16px',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => setIsEditing(false)}
                  style={{
                    backgroundColor: '#0073aa',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  {__('Done Editing - View Preview', 'puroclean-tw')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  // Show preview mode with ServerSideRender
  return (
    <>
      <InspectorControls>
        <PanelBody title={__('White Papers Settings', 'puroclean-tw')} initialOpen={true}>
          <button
            onClick={() => setIsEditing(true)}
            style={{
              backgroundColor: '#0073aa',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              width: '100%',
              marginBottom: '15px'
            }}
          >
            {__('Edit Content', 'puroclean-tw')}
          </button>
          
          {/* Show All Papers Toggle */}
          <ToggleControl
            label={__('Show All White Papers', 'puroclean-tw')}
            checked={attributes.showAllPapers}
            onChange={(value) => setAttributes({ showAllPapers: value })}
            help={__('Toggle to show all papers or select specific ones', 'puroclean-tw')}
          />

          {attributes.showAllPapers ? (
            /* Posts Per Page Setting */
            <RangeControl
              label={__('Number of Papers to Show', 'puroclean-tw')}
              value={attributes.postsPerPage}
              onChange={(value) => setAttributes({ postsPerPage: value })}
              min={1}
              max={12}
              help={__('How many white papers to display', 'puroclean-tw')}
            />
          ) : (
            /* White Paper Selection */
            <div style={{ marginTop: 12 }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
                {__("Pick White Papers", "puroclean-tw")} ({selected.length}/{maxSelect})
              </label>

              <TextControl
                placeholder={__("Search…", "puroclean-tw")}
                value={searchTerm}
                onChange={setSearchTerm}
              />

              {isLoading && <Spinner />}

              {!isLoading && (
                <div
                  style={{
                    maxHeight: 240,
                    overflow: "auto",
                    border: "1px solid #ddd",
                    borderRadius: 4,
                    padding: 8,
                    marginTop: 8,
                  }}
                >
                  {filteredOptions.length === 0 && (
                    <p style={{ margin: 0, color: "#666" }}>
                      {__("No results", "puroclean-tw")}
                    </p>
                  )}

                  {filteredOptions.map((opt) => (
                    <CheckboxControl
                      key={opt.value}
                      label={opt.label}
                      checked={selected.includes(opt.value)}
                      onChange={() => toggleSelect(opt.value)}
                      disabled={!selected.includes(opt.value) && selected.length >= maxSelect}
                    />
                  ))}
                </div>
              )}

              <p style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                {__("Select up to 6 items.", "puroclean-tw")}
              </p>
            </div>
          )}
        </PanelBody>
      </InspectorControls>

      <div {...blockProps}>
        <ServerSideRender
          block="puroclean/white-papers"
          attributes={attributes}
          urlQueryArgs={{ context: 'edit' }}
        />
      </div>
    </>
  )
}

registerBlockType('puroclean/white-papers', {
  edit: Edit,
  save() {
    return null; // Server-side rendered
  }
}); 